import type { Client } from '../../common/types/client';
import type { DecryptedItem } from '../../common/types/items';
import type { OptionStatistic, MeasureStatistic } from '../../common/types/statistics/fbStatistics';
import type { StatisticsFilter } from '../../common/types/statistics/statistics';
import type { Field } from '../../common/types/form';
import statistics from '../../common/helpers/statistics';
import sort from '../../common/helpers/sort';
import coreDataForm from '../../clients/detail/fbForm/coreDataForm';
import importantDataForm from '../../clients/detail/fbForm/importantDataForm';
import miscForm from '../../clients/detail/fbForm/miscForm';
import filter from '../../common/helpers/filter';
import formatter from '../../common/helpers/formatter';
import { ClientStatistic } from '../../common/types/statistics/fbStatistics';

const oneDay = 1000 * 60 * 60 * 24;

const clientOptionsKeyFieldsMapping: {
  key: keyof OptionStatistic;
  fields: Field<Client>[];
}[] = [
  {
    key: 'personType',
    fields: coreDataForm([]).fields,
  },
  {
    key: 'gender',
    fields: coreDataForm([]).fields,
  },
  {
    key: 'location',
    fields: coreDataForm([]).fields,
  },
  {
    key: 'charge',
    fields: importantDataForm.fields,
  },
  {
    key: 'highRisk',
    fields: importantDataForm.fields,
  },
  {
    key: 'profession',
    fields: importantDataForm.fields,
  },
  {
    key: 'consultationOccasion',
    fields: miscForm.fields,
  },
  {
    key: 'suspectedDiagnosis',
    fields: miscForm.fields,
  },
  {
    key: 'mentalDisability',
    fields: miscForm.fields,
  },
  {
    key: 'timeSinceAssault',
    fields: miscForm.fields,
  },
  {
    key: 'arrangedVia',
    fields: miscForm.fields,
  },
];

const getClientStatistic = (clients: DecryptedItem<Client>[]): OptionStatistic => {
  return clientOptionsKeyFieldsMapping.reduce(
    (optionStatistic, { key, fields }) => ({
      ...optionStatistic,
      [key]: statistics.getStatisticByOptions(clients, key, fields),
    }),
    {
      personType: {},
      gender: {},
      location: {},
      charge: {},
      highRisk: {},
      profession: {},
      consultationOccasion: {},
      suspectedDiagnosis: {},
      mentalDisability: {},
      timeSinceAssault: {},
      arrangedVia: {},
    },
  );
};

const defaultMeasure = {
  consultationLocally: 0,
  consultationLocallyWithChildcare: 0,
  outreachConsultation: 0,
  byPhone: 0,
  online: 0,
  support: 0,
  statement: 0,
  contactWithOtherProfessionals: 0,
  sendingOfLists: 0,
  sendingOfInfoMaterial: 0,
};

const getMeasureStatistics = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): MeasureStatistic => {
  const filteredMeasures = clients
    .filter((client) => {
      return statisticsFilter.advisor.length === 0 || client.payload.advisor === statisticsFilter.advisor;
    })
    .map((client) => {
      const measures = (client.payload.measures || []).filter((measure) =>
        filter.isInYear(measure.date, statisticsFilter.year),
      );
      return {
        ...client,
        measures,
      };
    });

  const totalNumberOfMeasures = filteredMeasures.reduce(
    (numberOfMeasures, client) => numberOfMeasures + client.measures.length,
    0,
  );

  const measures = (Object.keys(defaultMeasure) as Array<keyof typeof defaultMeasure>).reduce(
    (measures, measureKey) => {
      const numberOfMeasuresByType = filteredMeasures.map(
        (client) => client.measures.filter((measure) => measure.type === measureKey).length,
      );

      const numberOfMeasures = numberOfMeasuresByType.reduce((value, current) => value + current, 0);

      return {
        ...measures,
        [measureKey]: (measures[measureKey] || 0) + numberOfMeasures,
      };
    },
    defaultMeasure,
  );

  return {
    totalNumberOfMeasures,
    measures,
  };
};

const getTimesBetweenDates = (clients: DecryptedItem<Client>[]): Record<string, number> => {
  return clients.reduce((prevValue: Record<string, number>, client) => {
    const measures = (client.payload.measures || [])
      .filter((measure) => measure.date != null)
      .sort((aM, bM) => sort.sortByDate(aM.date, bM.date));
    if (client.payload.firstContactDate == null || measures.length === 0) {
      return prevValue;
    }
    const timeBetweenDates = `${
      (new Date(measures[0].date || '').getTime() - new Date(client.payload.firstContactDate || '').getTime()) / oneDay
    }`;

    const value = prevValue[timeBetweenDates];
    return value == null
      ? {
          ...prevValue,
          [timeBetweenDates]: 1,
        }
      : {
          ...prevValue,
          [timeBetweenDates]: value + 1,
        };
  }, {});
};

const getAgeStatistic = (clients: DecryptedItem<Client>[]): Record<string, number> => {
  return clients.reduce((prevValue: Record<string, number>, client) => {
    if (client.payload.birthday != null && client.payload.firstContactDate != null) {
      const age = formatter.getYearRange(client.payload.birthday, client.payload.firstContactDate);
      const label = age === 1 ? 'Jahr' : 'Jahre';
      const ageLabel = `${age} ${label}`;

      const value = prevValue[ageLabel];
      return value == null
        ? {
            ...prevValue,
            [ageLabel]: 1,
          }
        : {
            ...prevValue,
            [ageLabel]: value + 1,
          };
    }
    return prevValue;
  }, {});
};

const getFilteredClients = (
  clients: DecryptedItem<Client>[],
  statisticsFilter: StatisticsFilter,
): DecryptedItem<Client>[] => {
  return clients.filter((client) => {
    const isInDateRange = filter.isInYear(client.payload.firstContactDate, statisticsFilter.year);

    return (
      (statisticsFilter.advisor.length === 0 || client.payload.advisor === statisticsFilter.advisor) && isInDateRange
    );
  });
};

const createStatistic = (clients: DecryptedItem<Client>[], statisticsFilter: StatisticsFilter): ClientStatistic => {
  const filteredClients = getFilteredClients(clients, statisticsFilter);

  const timesBetweenDates = getTimesBetweenDates(filteredClients);
  const ageStatistic = getAgeStatistic(filteredClients);

  const numberOfMinors = statistics.getNumberOfValues(filteredClients, 'minorChildren');
  const numberOfSingleParents = statistics.getNumberOfValues(filteredClients, 'singleParent');
  const numberOfImmigrationBackground = statistics.getNumberOfValues(filteredClients, 'immigrationBackground');
  const numberOfFleeingBackground = statistics.getNumberOfValues(filteredClients, 'fleeingBackground');
  const numberOfTranslationNecessary = statistics.getNumberOfValues(filteredClients, 'translationNecessary');
  const numberOfPartnerHasMentalIllness = statistics.getNumberOfValues(filteredClients, 'partnerHasMentalIllness');
  const numberOfPartnerHasAddiction = statistics.getNumberOfValues(filteredClients, 'partnerHasAddiction');

  const optionStatistic = getClientStatistic(filteredClients);
  const measureStatistics = getMeasureStatistics(clients, statisticsFilter);

  return {
    totalNumber: filteredClients.length,
    timesBetweenDates,
    ageStatistic,
    numberOfMinors,
    numberOfSingleParents,
    numberOfImmigrationBackground,
    numberOfFleeingBackground,
    numberOfTranslationNecessary,
    numberOfPartnerHasMentalIllness,
    numberOfPartnerHasAddiction,
    ...optionStatistic,
    ...measureStatistics,
  };
};

export { clientOptionsKeyFieldsMapping };

export default createStatistic;
